import { Dispatch, RefObject } from 'react';

import { SessionAction, SessionActionTypes } from '@actions/sessionActions';
import { Opener } from '@hooks/useOpener';
import {
  BannerKindEnum,
  ConsultFragment as Consult,
  FocusAreaEnum,
  GetMeQuery,
  ResourceFragment,
  WorksheetFragment
} from '@typing/Generated';
import { IconName } from '@utils/iconUtils';

type Me =
  | Extract<GetMeQuery['me'], { __typename: 'Admin' }>
  | Extract<GetMeQuery['me'], { __typename: 'Client' }>
  | Extract<GetMeQuery['me'], { __typename: 'Expert' }>;

export interface SessionState {
  authToken: string | null;
  impersonateClientAuthToken: string | null;
  impersonateExpertAuthToken: string | null;
  impersonatingClient: boolean;
  impersonatingExpert: boolean;
  me: Me | undefined;
  otpVerified: boolean;
}

export enum NotificationType {
  VERIFIED_EMAIL = 'verifiedEmail'
}

export interface NotificationState {
  activeNotifications: Notification[];
}

export type SessionActionType = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
  type: SessionAction;
};

export type SessionContextType = {
  dispatch: Dispatch<SessionActionTypes>;
  sessionState: SessionState;
};

export type CarePlanFocusAreaContextType = {
  focusArea: FocusAreaEnum;
  setFocusArea: (focusArea: FocusAreaEnum) => void;
};

export type AppUpdatedContextType = {
  appUpdatedState: boolean;
  toggle: (appUpdated: boolean) => void;
};

export type ErrorContextType = {
  addError: (error: string) => void;
};

export type Banner = {
  bannerId?: string;
  buttonDisabled?: boolean;
  buttonOnClick?: () => void | null | Window;
  buttonTextKey?: string;
  clientId?: string;
  icon?: IconName;
  journeyId?: string;
  kind?: BannerKindEnum | undefined;
  onDismiss?: () => void;
  openerKind?: OpenerType;
  position: number;
  routerLink?: string;
  scheduleConsultButton?: boolean;
  text: string;
};

export type BannerContextType = {
  changeCurrentBanner: (banner: Banner) => void;
  currentBanner: Banner | undefined;
  deleteCurrentBanner: () => void;
  dismissBannerKind: (kind: BannerKindEnum) => void;
  sessionDismissedBannerKinds: string[];
};

export type OpenerContextType = {
  openerState: OpenerState;
  toggleOpener: (type: OpenerType, isOpen: boolean, record?: ResourceFragment | WorksheetFragment | undefined) => void;
};

export type OpenerState = {
  isOpen: boolean;
  record: ResourceFragment | WorksheetFragment | undefined;
  type: OpenerType;
}[];

export enum OpenerType {
  LEGAL = 'legal',
  MEDICATION = 'medication',
  MESSAGES = 'messages',
  PROVIDER = 'provider',
  SAFETY = 'safety',
  TAGS = 'tags',
  TASKS = 'tasks',
  TIME_LOG = 'time_log'
}

export enum ScrollPositionPageType {
  CARE_PLAN = 'care_plan',
  DOCUMENTS = 'documents',
  GOAL_TEMPLATES = 'goal_templates',
  HOME = 'home',
  LEARN = 'learn',
  MARKETPLACE = 'marketplace',
  WORKSHEET_TEMPLATES = 'worksheet_templates'
}

export type ScrollPositionState = {
  currentRef: RefObject<HTMLIonContentElement> | null;
  currentType: ScrollPositionPageType | null;
  pages: {
    loading: boolean;
    topPosition: number;
    type: ScrollPositionPageType;
  }[];
};

export type ScrollPositionContextType = {
  scrollPositionState: ScrollPositionState;
  scrollToSavedPosition: () => void;
  setContentRef: (ref: RefObject<HTMLIonContentElement>) => void;
  setCurrentType: (type: ScrollPositionPageType | null) => void;
  setTopPosition: (topPosition: number) => void;
};

export type PopoverContextType = {
  hidePopover: (content: React.ReactNode, event: React.MouseEvent) => void;
  popoverState: PopoverState;
  showPopover: (content: React.ReactNode, event: React.MouseEvent) => void;
};

export type PopoverState = {
  bottomMost: boolean | null;
  content?: React.ReactNode;
  isOpen: boolean;
  rightMost: boolean;
  xPosition: number;
  yPosition: number;
};

export type ScheduleConsultModalContextType = {
  hideScheduleConsultModal: () => void;
  scheduleConsultModalState: ScheduleConsultModalState;
  showScheduleConsultModal: (data: ScheduleConsultModalData) => void;
};

export type ScheduleConsultModalData = {
  clientId: string;
  defaultDate?: Date | null;
  journeyId: string;
  reschedulingConsult?: Consult;
};

export type ScheduleConsultModalState = {
  data: ScheduleConsultModalData | null;
  isOpen: boolean;
  opener: Opener | null;
};

export type FullscreenModalType = {
  content: React.ReactNode;
  id: string;
  isOpen: boolean;
  name: string;
  renderCount: number;
};

export type FullscreenModalState = {
  modals: FullscreenModalType[];
};

export type FullscreenModalContextType = {
  fullscreenModalState: FullscreenModalState;
  setFullscreenModalState: (state: FullscreenModalState) => void;
};

export type CookieConsentContextType = {
  acceptsAnalyticsCookies: boolean | null;
  acceptsFunctionalCookies: boolean | null;
  acceptsNecessaryCookies: boolean | null;
  acceptsPaymentCookies: boolean | null;
};
